import { Condition } from "@medplum/fhirtypes";

import { createEntityAdapter, createSelector, createSlice } from "@reduxjs/toolkit";
import orderBy from "lodash/orderBy";

import {
  filterProblemsBadgesByVisibility,
  filterProblemsByType,
} from "@/clients/zod-schemas/problems";
import { ProblemType } from "@/fhir/enums/quilted-health";

import type { AppDispatch, RootState } from "..";

const problemsAdapter = createEntityAdapter({
  selectId: (problem: Condition) => problem.id!,
});

const reducers = {
  loadProblems: problemsAdapter.setAll,
  createManyProblems: problemsAdapter.addMany,
  createProblem: problemsAdapter.addOne,
  removeProblem: problemsAdapter.removeOne,
  updateProblem: problemsAdapter.updateOne,
  upsertProblems: problemsAdapter.upsertMany,
};

export const problemsSlice = createSlice({
  name: "clientProblems",
  initialState: problemsAdapter.getInitialState(),
  reducers,
});

export const {
  loadProblems,
  createProblem,
  createManyProblems,
  removeProblem,
  updateProblem,
  upsertProblems,
} = problemsSlice.actions;

/** Prefer the version with the newer meta.lastUpdated when merging; when equal, keep current so a just-saved display toggle is not overwritten by poll. */
function mergeProblemsPreferNewer(current: Condition[], polled: Condition[]): Condition[] {
  const byId = new Map<string, Condition>();
  for (const c of polled) byId.set(c.id!, c);
  for (const c of current) {
    const polledVersion = byId.get(c.id!);
    const currentUpdated = c.meta?.lastUpdated ? new Date(c.meta.lastUpdated).getTime() : 0;
    const polledUpdated = polledVersion?.meta?.lastUpdated
      ? new Date(polledVersion.meta.lastUpdated).getTime()
      : 0;
    if (!polledVersion || currentUpdated >= polledUpdated) byId.set(c.id!, c);
  }
  return Array.from(byId.values());
}

/**
 * Merges polled conditions with current store, preferring the version with
 * newer meta.lastUpdated so a just-saved "Display on client list" change
 * is not overwritten by stale poll data.
 */
export const mergeAndLoadProblemsFromPoll =
  (polledConditions: Condition[]) => (dispatch: AppDispatch, getState: () => RootState) => {
    const current = Object.values(getState().clientProblems.entities) as Condition[];
    const merged = mergeProblemsPreferNewer(current, polledConditions);
    dispatch(loadProblems(merged));
  };

const selectProblemsEntities = (state: RootState) => state.clientProblems.entities;

export const selectSortedProblemsList = createSelector(
  [selectProblemsEntities],
  (clientProblemsEntities): Condition[] => {
    return orderBy(
      Object.values(clientProblemsEntities),
      ["clinicalStatus.text", "onsetDateTime"],
      ["asc", "desc"],
    );
  },
);

export const selectNonPregnancyProblems = createSelector(
  [selectSortedProblemsList],
  (allProblems): Condition[] => {
    return filterProblemsByType(allProblems, ProblemType.NonPregnancyProblem);
  },
);

export const selectVisibleNonPregnancyProblems = createSelector(
  [selectNonPregnancyProblems],
  (matchedProblems): string[] => {
    return filterProblemsBadgesByVisibility(matchedProblems, ProblemType.NonPregnancyProblem);
  },
);

export const selectIsDiagnosisInProblemList = createSelector(
  [selectVisibleNonPregnancyProblems],
  (visibleProblems) => (diagnosisDisplay: string) => {
    return visibleProblems.some((problemName) => problemName === diagnosisDisplay);
  },
);

export default problemsSlice.reducer;
